// app/src/screens/SignIn.jsx — the sign-in frame, D-AUTH parity §1: ALL THREE methods
// (email/password + Google + LinkedIn, mirroring Quocia-FB's SignInForm + OAuth links),
// plus the link to the real signup page. The local rig keeps one-click dev sign-in.
// D2a Spectrum styling preserved.

import React, { useState } from 'react';
import { Link } from '@common/router.js';
import { useI18n } from '../i18n/index.jsx';
import { useSession } from '../lib/session.jsx';
import { useSeedLangCookie } from '../lib/locale-sync.jsx';
import { startOAuth } from '../lib/oauth.js';

export const INPUT_STYLE = { inlineSize: '100%', padding: '12px 14px', marginBlockEnd: 8, border: '1px solid var(--border)', borderRadius: 10, font: '400 14px var(--font-body)', background: 'var(--surface)', boxSizing: 'border-box' };

// the shared auth-page chrome (logo + title card) — signup reuses it
export function AuthFrame({ title, subtitle, children }) {
  const { t } = useI18n();
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--paper)', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 380, textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9, marginBottom: 20 }}>
          <img src="/logo-mark.png" alt="" style={{ width: 30, height: 30 }} />
          <span style={{ font: '700 22px var(--font-body)', letterSpacing: '-.02em' }}>{t('app.short')}</span>
        </div>
        <h1 className="h-hero" style={{ marginBottom: 8 }}>{title}</h1>
        <p className="muted" style={{ fontSize: 15, lineHeight: 1.5, margin: '0 0 24px' }}>{subtitle}</p>
        <div className="q-card" style={{ padding: 22, textAlign: 'start', boxShadow: 'var(--shadow-card)' }}>
          {children}
        </div>
      </div>
    </div>
  );
}

export function OrDivider() {
  const { t } = useI18n();
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0', color: 'var(--text-faint)', fontSize: 12 }}>
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} /><span className="mono">{t('signin.or')}</span><span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    </div>
  );
}

export default function SignIn() {
  const { t } = useI18n();
  const { auth } = useSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState(null);
  useSeedLangCookie(); // a UAU on an auth page leaves with the shared `lang` cookie (D-AUTH §3)
  const guard = (fn) => async () => { setErr(null); try { await fn(); } catch (e) { setErr(e.message); } };

  return (
    <AuthFrame title={t('signin.title')} subtitle={t('signin.subtitle')}>
      {auth.mode === 'dev' ? (
        <button className="q-btn press btn-primary" style={{ width: '100%' }} onClick={guard(() => auth.signInDev())}>{t('signin.dev')}</button>
      ) : (
        <>
          <button className="q-btn press btn-primary" style={{ width: '100%' }} onClick={() => startOAuth('google')}>{t('signin.google')}</button>
          <button className="q-btn press btn-ghost" style={{ width: '100%', marginBlockStart: 8 }} onClick={() => startOAuth('linkedin')}>{t('signin.linkedin')}</button>
          <OrDivider />
          <input type="email" placeholder={t('signin.emailPlaceholder')} value={email} onChange={(e) => setEmail(e.target.value)} style={INPUT_STYLE} />
          <input type="password" placeholder={t('signin.passwordPlaceholder')} value={password} onChange={(e) => setPassword(e.target.value)} style={INPUT_STYLE} />
          <button className="q-btn press btn-ghost" style={{ width: '100%', marginBlockStart: 4 }} onClick={guard(() => auth.signInEmail(email, password))}>{t('signin.email')}</button>
          <p className="muted" style={{ fontSize: 13, textAlign: 'center', margin: '14px 0 0' }}>
            {t('signin.noAccount')} <Link to="/signup" style={{ color: 'var(--ink)', fontWeight: 600 }}>{t('signin.signupLink')}</Link>
          </p>
        </>
      )}
      {err && <p style={{ color: 'var(--coral)', fontSize: 13, margin: '12px 0 0' }}>{err}</p>}
    </AuthFrame>
  );
}
